import { useEffect } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Loader, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { TutorialDialog } from '@/_shared/components/TutorialDialog';
import { TutorialKeys } from '@/_shared/constants';
import { FeatureFlag } from '@/_shared/constants/featureFlags';
import { useTutorialCheck } from '@/_shared/hooks';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useFeatureFlags } from '@/_shared/hooks/useFeatureFlag';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getUserProfileCall } from '@/core/state/api';
import { getEmployeesMeCall } from '@/profile';
import {
  getEmployerNotCompletedSurveysCall,
  postEmployerByIdSurveyResultCall,
} from '@/surveys/state/api';
import { SurveyNotCompleted } from '../SurveyNotCompleted';
import { SurveyResultsManager } from '../SurveyResultsManager';
import { TeamList } from '../TeamList';
import { TeamsDepartment } from '../TeamsDepartment';

export const HomepageManager = () => {
  const t = useTranslations('dashboard.learning');
  const tShared = useTranslations();

  const { isFeatureEnabled } = useFeatureFlags();
  const isChatBotEnabled = isFeatureEnabled(FeatureFlag.CHATBOT);

  const { hasAlreadySeenTutorial } = useTutorialCheck();

  const { genericResponsiveValue } = useResponsiveStyle();

  const { firstName, email } = getUserProfileCall.hook();

  const { openDialog: openDialogManagerTutorial } = useDialog('managerLearningTutorial');

  const { hasManagerLeader, hasTeamLeader } = getEmployeesMeCall.hook();
  const { userHasSurveysToComplete } = getEmployerNotCompletedSurveysCall.hook();
  const {
    loading: loadingPostEmployerByIdSurveyResult,
    success: successPostEmployerByIdSurveyResult,
    error: errorPostEmployerByIdSurveyResult,
    execute: executePostEmployerByIdSurveyResult,
    hasCompletedSurvey,
  } = postEmployerByIdSurveyResultCall.hook();

  const slidesManager = [
    {
      title: t('tutorial.manager.slides.slide1.title'),
      description: t('tutorial.manager.slides.slide1.description'),
      assetName: 'tutorialManagerSlide1',
    },
    {
      title: t('tutorial.manager.slides.slide2.title'),
      description: t('tutorial.manager.slides.slide2.description'),
      assetName: 'tutorialManagerSlide2',
    },
    {
      title: t('tutorial.manager.slides.slide3.title'),
      description: t('tutorial.manager.slides.slide3.description'),
      assetName: 'tutorialManagerSlide3',
    },
    {
      title: t('tutorial.manager.slides.slide4.title'),
      description: t('tutorial.manager.slides.slide4.description'),
      assetName: 'tutorialManagerSlide4',
    },
    ...(isChatBotEnabled
      ? [
          {
            title: t('tutorial.manager.slides.slide5.title'),
            description: t('tutorial.manager.slides.slide5.description'),
            assetName: 'tutorialManagerSlide5',
          },
        ]
      : []),
  ];

  const slidesTeam = [
    {
      title: t('tutorial.team.slides.slide1.title'),
      description: t('tutorial.team.slides.slide1.description'),
      assetName: 'tutorialTeamSlide1',
    },
    {
      title: t('tutorial.team.slides.slide2.title'),
      description: t('tutorial.team.slides.slide2.description'),
      assetName: 'tutorialTeamSlide2',
    },
    {
      title: t('tutorial.team.slides.slide3.title'),
      description: t('tutorial.team.slides.slide3.description'),
      assetName: 'tutorialTeamSlide3',
    },
    {
      title: t('tutorial.team.slides.slide4.title'),
      description: t('tutorial.team.slides.slide4.description'),
      assetName: 'tutorialTeamSlide4',
    },
    ...(isChatBotEnabled
      ? [
          {
            title: t('tutorial.team.slides.slide5.title'),
            description: t('tutorial.team.slides.slide5.description'),
            assetName: 'tutorialTeamSlide5',
          },
        ]
      : []),
  ];

  useEffect(() => {
    executePostEmployerByIdSurveyResult({
      username: email,
    });
  }, [email, executePostEmployerByIdSurveyResult]);

  useEffect(() => {
    if (!hasAlreadySeenTutorial(TutorialKeys.Manager)) {
      openDialogManagerTutorial();
    }
  }, [hasAlreadySeenTutorial, openDialogManagerTutorial]);

  if (loadingPostEmployerByIdSurveyResult) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorPostEmployerByIdSurveyResult) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge iconName='info' type='error' label={message} />;
  }

  if (
    successPostEmployerByIdSurveyResult &&
    !loadingPostEmployerByIdSurveyResult &&
    !errorPostEmployerByIdSurveyResult &&
    email
  ) {
    return (
      <Box>
        <Box
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'row' },
            defaultValue: 'column',
          })}
        >
          <Box flex='2' minWidth={0}>
            <Typography mb='xxs' variant='h1'>
              {t('title', { firstName })}
            </Typography>
            <Typography mb='s' variant='p3/light'>
              {!hasCompletedSurvey && <>{t('description.hasNeverCompletedSurveys')}</>}
              {userHasSurveysToComplete && hasCompletedSurvey && (
                <>{t('description.hasNotCompletedSurveys')}</>
              )}
              {!userHasSurveysToComplete && hasCompletedSurvey && (
                <>{t('description.hasCompletedSurveys')}</>
              )}
            </Typography>
            {userHasSurveysToComplete && <SurveyNotCompleted />}
            {hasCompletedSurvey && <SurveyResultsManager />}
          </Box>
          {(hasManagerLeader || hasTeamLeader) && (
            <Box
              ml={genericResponsiveValue({
                breakPoints: { desktop: 's' },
                defaultValue: 'none',
              })}
              flex='1'
            >
              {hasManagerLeader && <TeamsDepartment />}
              {hasTeamLeader && <TeamList />}
            </Box>
          )}
        </Box>
        <TutorialDialog
          translationsKey='dashboard.learning.tutorial.manager'
          dialogKey='managerLearningTutorial'
          slides={hasManagerLeader ? slidesManager : slidesTeam}
        />
      </Box>
    );
  }

  return null;
};
